import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { C, css } from './Dashboard';

// Admin-facing management UI for shareable, email-gated Revenue Dashboard
// links (board members, family — see supabase/migrations/20260909_dashboard_shares.sql
// and src/PublicSharedDashboard.js for the full flow). Reads/writes
// dashboard_shares and dashboard_share_sessions directly via supabase-js —
// no edge function needed here, RLS (is_org_admin) already scopes this
// correctly for a logged-in admin, unlike the three public-facing steps a
// shared viewer goes through.

const APP_ORIGIN = window.location.origin;

function shareUrl(token) { return `${APP_ORIGIN}/shared/dashboard/${token}`; }

export default function DashboardShareModal({ org, onClose }) {
  const [shares, setShares] = useState([]);
  const [sessions, setSessions] = useState({}); // share_id -> sessions[]
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState('');
  const [scope, setScope] = useState('full');
  const [emailsText, setEmailsText] = useState('');
  const [creating, setCreating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    if (!org?.id) return;
    setLoading(true);
    const { data, error } = await supabase.from('dashboard_shares').select('*').eq('org_id', org.id).order('created_at', { ascending: false });
    if (!error) setShares(data || []);
    setLoading(false);
  }, [org?.id]);

  useEffect(() => { load(); }, [load]);

  const loadSessions = async (shareId) => {
    const { data } = await supabase.from('dashboard_share_sessions').select('*').eq('share_id', shareId).order('created_at', { ascending: false });
    setSessions(s => ({ ...s, [shareId]: data || [] }));
  };

  const toggleExpand = (share) => {
    if (expandedId === share.id) { setExpandedId(null); return; }
    setExpandedId(share.id);
    if (!sessions[share.id]) loadSessions(share.id);
  };

  const parseEmails = (text) => [...new Set(
    text.split(/[\n,;]/).map(e => e.trim().toLowerCase()).filter(e => e && e.includes('@'))
  )];

  const createShare = async () => {
    const emails = parseEmails(emailsText);
    if (emails.length === 0) { setErrorMsg('Add at least one email address.'); return; }
    setCreating(true); setErrorMsg('');
    const { error } = await supabase.from('dashboard_shares').insert({
      org_id: org.id, label: label.trim() || null, scope, allowed_emails: emails,
    });
    setCreating(false);
    if (error) { setErrorMsg('Could not create the share — ' + error.message); return; }
    setLabel(''); setScope('full'); setEmailsText(''); setShowCreate(false);
    load();
  };

  const revokeShare = async (share) => {
    await supabase.from('dashboard_shares').update({ revoked_at: new Date().toISOString() }).eq('id', share.id);
    load();
  };
  const restoreShare = async (share) => {
    await supabase.from('dashboard_shares').update({ revoked_at: null }).eq('id', share.id);
    load();
  };
  const deleteShare = async (share) => {
    await supabase.from('dashboard_shares').delete().eq('id', share.id);
    setConfirmDelete(null);
    load();
  };
  const revokeSession = async (shareId, sessionId) => {
    await supabase.from('dashboard_share_sessions').update({ revoked_at: new Date().toISOString() }).eq('id', sessionId);
    loadSessions(shareId);
  };

  const removeEmail = async (share, email) => {
    const next = (share.allowed_emails || []).filter(e => e.toLowerCase() !== email.toLowerCase());
    await supabase.from('dashboard_shares').update({ allowed_emails: next }).eq('id', share.id);
    load();
  };
  const addEmail = async (share, email) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) return;
    const existing = (share.allowed_emails || []).map(e => e.toLowerCase());
    if (existing.includes(trimmed)) return;
    await supabase.from('dashboard_shares').update({ allowed_emails: [...(share.allowed_emails || []), trimmed] }).eq('id', share.id);
    load();
  };

  const copyLink = (share) => {
    navigator.clipboard.writeText(shareUrl(share.token));
    setCopiedId(share.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000,padding:'20px'}} onClick={onClose}>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'12px',padding:'28px',maxWidth:'640px',width:'100%',maxHeight:'85vh',overflow:'auto'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'6px'}}>
          <div style={{fontSize:'17px',fontWeight:'800',color:C.textBright}}>Share Dashboard</div>
          <button onClick={onClose} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:'18px',lineHeight:1}}>✕</button>
        </div>
        <div style={{fontSize:'12px',color:C.muted,marginBottom:'20px',lineHeight:'1.5'}}>
          Give specific people — board members, family — a link to a read-only version of this dashboard. They verify their email (no TimeKeep account needed) and stay signed in for 30 days.
        </div>

        {!showCreate ? (
          <button style={{...css.btn('primary'),marginBottom:'20px'}} onClick={()=>setShowCreate(true)}>+ New Share Link</button>
        ) : (
          <div style={{...css.card,marginBottom:'20px'}}>
            <div style={{marginBottom:'12px'}}>
              <div style={css.lbl}>Label (optional)</div>
              <input style={css.inp} value={label} onChange={e=>setLabel(e.target.value)} placeholder="e.g. Board, My wife" />
            </div>
            <div style={{marginBottom:'12px'}}>
              <div style={css.lbl}>What they see</div>
              <div style={{display:'flex',gap:'8px'}}>
                <button style={{...css.btn(scope==='full'?'primary':'ghost'),flex:1,border:`1px solid ${scope==='full'?C.accent:C.border}`}} onClick={()=>setScope('full')}>
                  Full dashboard
                </button>
                <button style={{...css.btn(scope==='summary'?'primary':'ghost'),flex:1,border:`1px solid ${scope==='summary'?C.accent:C.border}`}} onClick={()=>setScope('summary')}>
                  Summary only
                </button>
              </div>
              <div style={{fontSize:'11px',color:C.muted,marginTop:'6px'}}>
                {scope==='full'
                  ? 'Everything you see: client names, per-client $, contractor utilization.'
                  : 'Headline KPIs and profit only — no client or contractor detail, ever, even in the underlying data.'}
              </div>
            </div>
            <div style={{marginBottom:'4px'}}>
              <div style={css.lbl}>Who can view (one email per line)</div>
              <textarea style={{...css.inp,minHeight:'70px',resize:'vertical',fontFamily:'inherit'}} value={emailsText}
                onChange={e=>{setEmailsText(e.target.value);setErrorMsg('');}}
                placeholder={"jane@example.com\njohn@example.com"} />
            </div>
            {errorMsg && <div style={{fontSize:'12px',color:C.danger,marginBottom:'10px'}}>{errorMsg}</div>}
            <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
              <button style={css.btn('primary')} onClick={createShare} disabled={creating}>{creating?'Creating…':'Create Link'}</button>
              <button style={css.btn('ghost')} onClick={()=>{setShowCreate(false);setErrorMsg('');}}>Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{color:C.muted,fontSize:'13px',padding:'12px 0'}}>Loading…</div>
        ) : shares.length === 0 ? (
          <div style={{color:C.muted,fontSize:'13px',padding:'12px 0'}}>No share links yet.</div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {shares.map(share => (
              <div key={share.id} style={{...css.card,marginBottom:0,opacity:share.revoked_at?0.6:1}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'10px',flexWrap:'wrap'}}>
                  <div>
                    <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                      <span style={{fontWeight:'700',color:C.textBright,fontSize:'14px'}}>{share.label || 'Untitled share'}</span>
                      <span style={css.badge(share.scope==='full'?'blue':'purple')}>{share.scope==='full'?'Full':'Summary'}</span>
                      {share.revoked_at && <span style={css.badge('red')}>Revoked</span>}
                    </div>
                    <div style={{fontSize:'11px',color:C.muted,marginTop:'4px'}}>
                      {(share.allowed_emails||[]).length} {(share.allowed_emails||[]).length===1?'person':'people'} · created {new Date(share.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                    {!share.revoked_at && (
                      <button style={css.smBtn(C.accent)} onClick={()=>copyLink(share)}>{copiedId===share.id?'Copied ✓':'Copy Link'}</button>
                    )}
                    <button style={css.smBtn()} onClick={()=>toggleExpand(share)}>{expandedId===share.id?'Hide':'Manage'}</button>
                    {share.revoked_at ? (
                      <button style={css.smBtn(C.success)} onClick={()=>restoreShare(share)}>Restore</button>
                    ) : (
                      <button style={css.smBtn(C.danger)} onClick={()=>revokeShare(share)}>Revoke</button>
                    )}
                  </div>
                </div>

                {expandedId === share.id && (
                  <div style={{marginTop:'14px',paddingTop:'14px',borderTop:`1px solid ${C.border}`}}>
                    <div style={css.lbl}>Allowed emails</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'10px'}}>
                      {(share.allowed_emails||[]).map(e => (
                        <span key={e} style={{display:'flex',alignItems:'center',gap:'5px',background:C.bg,border:`1px solid ${C.border}`,borderRadius:'999px',padding:'3px 6px 3px 10px',fontSize:'11px',color:C.text}}>
                          {e}
                          <button onClick={()=>removeEmail(share, e)} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:'12px',padding:'0 2px'}}>✕</button>
                        </span>
                      ))}
                    </div>
                    <AddEmailRow onAdd={(e)=>addEmail(share, e)} />

                    <div style={{...css.lbl,marginTop:'16px'}}>Viewers</div>
                    {!sessions[share.id] ? (
                      <div style={{fontSize:'12px',color:C.muted}}>Loading…</div>
                    ) : sessions[share.id].length === 0 ? (
                      <div style={{fontSize:'12px',color:C.muted}}>Nobody has verified access yet.</div>
                    ) : (
                      <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                        {sessions[share.id].map(s => (
                          <div key={s.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'12px',color:C.textDim,background:C.bg,border:`1px solid ${C.border}`,borderRadius:'6px',padding:'6px 10px'}}>
                            <span>{s.email}{s.revoked_at ? ' (revoked)' : ''}</span>
                            <span style={{display:'flex',alignItems:'center',gap:'8px'}}>
                              <span style={{color:C.muted}}>{s.last_seen_at ? `Last seen ${new Date(s.last_seen_at).toLocaleDateString()}` : 'Not viewed yet'}</span>
                              {!s.revoked_at && <button style={css.smBtn(C.danger)} onClick={()=>revokeSession(share.id, s.id)}>Revoke</button>}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{marginTop:'16px'}}>
                      {confirmDelete === share.id ? (
                        <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                          <span style={{fontSize:'12px',color:C.danger}}>Delete this share permanently?</span>
                          <button style={css.smBtn(C.danger)} onClick={()=>deleteShare(share)}>Yes, delete</button>
                          <button style={css.smBtn()} onClick={()=>setConfirmDelete(null)}>Cancel</button>
                        </div>
                      ) : (
                        <button style={css.smBtn(C.muted)} onClick={()=>setConfirmDelete(share.id)}>Delete this share…</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AddEmailRow({ onAdd }) {
  const [value, setValue] = useState('');
  const submit = () => { if (value.trim()) { onAdd(value); setValue(''); } };
  return (
    <div style={{display:'flex',gap:'6px'}}>
      <input style={{...css.inp,fontSize:'12px',padding:'6px 10px'}} value={value}
        onChange={e=>setValue(e.target.value)} onKeyDown={e=>e.key==='Enter'&&submit()}
        placeholder="add-an-email@example.com" />
      <button style={css.smBtn(C.accent)} onClick={submit}>Add</button>
    </div>
  );
}
